/**
 * Styled single-sheet Excel writer.
 *
 * The workspace exports (chart queue, chart history) used to be written with
 * SheetJS (`xlsx`), whose community edition cannot style cells at all — no
 * fills, fonts or borders. This writes the same data with `exceljs` instead so
 * the workbook reads as "our app" rather than a default Excel grid:
 *
 *   • header row   — solid brand blue, white bold text
 *   • data rows    — alternating white / pale blue, hairline borders
 *   • real Excel table (filter dropdowns + structured reference), with Excel's
 *     OWN table theme switched OFF so it can't override the palette below
 *   • frozen header, auto-fitted column widths
 *
 * Only the WRITING layer lives here. Callers keep owning their columns, values
 * and filename — this never touches what goes into the file, only how it looks.
 */

/**
 * Brand palette as Excel ARGB. Mirrors the CSS custom properties in
 * `src/index.css` (`--primary`, `--primary-50`, …) and the THEME block in
 * client-admin's export-overview-excel.ts. Update alongside the palette.
 */
export const EXCEL_THEME = {
  /** --primary #2454c0 — header fill */
  primary: 'FF2454C0',
  /** --primary-50 #eff6ff — the pale blue band */
  primarySoft: 'FFEFF6FF',
  /** plain white band */
  white: 'FFFFFFFF',
  /** hairline grid, matches --border */
  border: 'FFDDE2EF',
  /** --text-primary */
  textPrimary: 'FF0B1324',
} as const

export interface StyledSheetColumn {
  /** Header text — must match the key used in each row object. */
  label: string
}

export interface StyledSheetSpec {
  /** Worksheet name, e.g. 'Chart Queue'. */
  sheetName: string
  /** Excel table name — letters/digits/underscore only, no spaces. */
  tableName: string
  columns: StyledSheetColumn[]
  rows: Array<Record<string, string | number>>
  /** Download filename, including the .xlsx extension. */
  fileName: string
}

/** Width heuristic kept identical to the previous SheetJS implementation. */
function columnWidth(label: string, rows: Array<Record<string, string | number>>): number {
  const maxDataLen = rows.reduce((max, row) => {
    const val = String(row[label] ?? '')
    return Math.max(max, val.length)
  }, 0)
  return Math.min(Math.max(label.length, maxDataLen) + 2, 40)
}

/**
 * Build and download a styled one-sheet workbook.
 *
 * exceljs is dynamically imported so it stays out of the main bundle — the
 * download path is the only place that needs it.
 */
export async function writeStyledSheet(spec: StyledSheetSpec): Promise<void> {
  const ExcelJS = (await import('exceljs')).default
  const workbook = new ExcelJS.Workbook()
  const ws = workbook.addWorksheet(spec.sheetName, {
    views: [{ state: 'frozen', ySplit: 1 }],
  })

  // Safety net: never emit the same header twice. Callers assemble their column
  // list from several sources (fixed columns + user-toggled ones) and can repeat
  // one — the old SheetJS writer hid that, because rows were keyed objects where
  // a repeated key simply overwrote itself. An ordered header array does not, so
  // a duplicate would become two identical columns in the workbook. Keep the
  // first occurrence, preserving column order.
  const headers = spec.columns
    .map((c) => c.label)
    .filter((label, i, all) => all.indexOf(label) === i)

  // A real Excel table gives filter dropdowns + the Table Design tab. `theme:
  // null` + no stripes stops Excel painting its own preset colours over the
  // brand fills applied below. addTable writes the header AND the data rows, so
  // nothing else adds rows.
  ws.addTable({
    name: spec.tableName,
    ref: 'A1',
    headerRow: true,
    // exceljs accepts `theme: null` at runtime to mean "no built-in table
    // style", but its types only allow the named presets — hence the cast.
    style: { theme: null, showRowStripes: false } as unknown as Parameters<
      typeof ws.addTable
    >[0]['style'],
    columns: headers.map((h) => ({ name: h, filterButton: true })),
    rows: spec.rows.map((row) => headers.map((h) => row[h] ?? '')),
  })

  // Column widths (set after addTable so the table doesn't reset them).
  headers.forEach((h, i) => {
    ws.getColumn(i + 1).width = columnWidth(h, spec.rows)
  })

  // Header row — brand blue, white bold.
  const headerRow = ws.getRow(1)
  headerRow.height = 22
  headerRow.eachCell((cell) => {
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: EXCEL_THEME.white } }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXCEL_THEME.primary } }
    cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }
    cell.border = {
      top: { style: 'thin', color: { argb: EXCEL_THEME.primary } },
      bottom: { style: 'thin', color: { argb: EXCEL_THEME.primary } },
      left: { style: 'thin', color: { argb: EXCEL_THEME.primary } },
      right: { style: 'thin', color: { argb: EXCEL_THEME.primary } },
    }
  })

  // Data rows — alternating white / pale blue, hairline borders.
  spec.rows.forEach((_row, idx) => {
    const excelRow = ws.getRow(idx + 2) // +1 header, +1 for 1-based rows
    const stripe = idx % 2 === 0 ? EXCEL_THEME.white : EXCEL_THEME.primarySoft
    excelRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { name: 'Calibri', size: 10, color: { argb: EXCEL_THEME.textPrimary } }
      cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: stripe } }
      cell.border = {
        top: { style: 'hair', color: { argb: EXCEL_THEME.border } },
        bottom: { style: 'hair', color: { argb: EXCEL_THEME.border } },
        left: { style: 'hair', color: { argb: EXCEL_THEME.border } },
        right: { style: 'hair', color: { argb: EXCEL_THEME.border } },
      }
    })
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = spec.fileName
  a.click()
  URL.revokeObjectURL(url)
}
